import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useCategoryTreeQuery } from '@/entities/category';
import { useExhibitionQuery } from '@/entities/exhibition';
import {
  hasActiveStoreFilters,
  type StoreProductsParams,
  useStoreProductsPage,
} from '@/entities/product';
import { Button } from '@/shared/ui';
import { ProductCardGrid, ProductCardGridSkeleton } from '@/widgets/product-card-grid';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';
import { StoreCategoryTabs } from '@/widgets/store-category-tabs';
import {
  StoreExhibitionBanner,
  StoreExhibitionBannerPlaceholder,
} from '@/widgets/store-exhibition-banner';
import { StoreExhibitionEnded } from '@/widgets/store-exhibition-ended';
import { StorePagination } from '@/widgets/store-pagination';
import { StoreFilterDrawer } from '@/widgets/store-sidebar';
import { StoreActiveFilters, StoreToolbar } from '@/widgets/store-toolbar';
import { parseStoreSearchParams, toStoreSearchParams } from './store-search-params';

/**
 * 끝난 기획전(숨김·삭제·공개 상품 0)만 NOT_FOUND 로 온다 — 그때만 종료 안내를 보여 준다.
 * 훅이 retry 를 꺼 두어 타임아웃·5xx 도 한 번에 올라오는데,
 * 그것까지 "종료" 로 읽으면 멀쩡한 기획전을 끝난 것으로 단정하게 된다(store-detail-page 와 같은 판별).
 */
const isExhibitionGone = (code: unknown): boolean => code === 'NOT_FOUND';

/**
 * 스토어 상품 목록(/store) — 필터·정렬·페이지 상태는 URL 쿼리가 단일 원천.
 * HOME 과 같은 디자인 언어로 선다 — 위에 붙는 흰 헤더 줄, 전폭 여백(가운데 폭 제한 없음), 그래파이트 푸터.
 * 순서: 큰 "Store" 제목·설명, 카테고리 탭(대분류 밑줄 + 소분류 알약), 툴바(총 수·정렬·필터), 걸린 조건 칩,
 * 기획전 띠, 상품 진열(4열), 페이지네이션. 색상·수량·단가는 툴바의 "필터"가 여는 오른쪽 드로어에서 고른다.
 */
export const StorePage = (): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseStoreSearchParams(searchParams), [searchParams]);

  const categoryTreeQuery = useCategoryTreeQuery();
  const productsPage = useStoreProductsPage(filters);
  // 일시 오류는 종료가 아니다 — 이름 띠만 빠지고 목록은 그대로 둔다(products.list 가 조건을 적용한다).
  const exhibitionQuery = useExhibitionQuery(filters.exhibitionId);
  const isExhibitionEnded =
    filters.exhibitionId !== undefined && isExhibitionGone(exhibitionQuery.error?.data?.code);
  // 종료 안내가 목록 자리를 대신하지 않을 때만 목록에 딸린 띠·페이지 이동 오류·페이지네이션을 보인다.
  const shouldShowListing = !isExhibitionEnded;
  // 기획전 링크로 들어와 이름을 기다리는 중 — 띠 자리를 회색 칸으로 잡아 둔다.
  // 조건이 없어 요청이 꺼진 쿼리도 isPending 이 true 라 파라미터 유무를 함께 본다.
  const isExhibitionTitlePending = filters.exhibitionId !== undefined && exhibitionQuery.isPending;
  // 종료 안내가 목록 자리를 대신하면 목록 로딩은 알리지 않는다.
  const isListLoading = shouldShowListing && productsPage.isPending;
  // 색상·수량·단가 조건은 모든 폭에서 오른쪽 드로어로 연다.
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  // 드로어의 포커스 효과가 목록 리렌더마다 다시 돌지 않게 콜백을 고정한다.
  const openFilterDrawer = useCallback(() => setIsFilterDrawerOpen(true), []);
  const closeFilterDrawer = useCallback(() => setIsFilterDrawerOpen(false), []);

  // 필터·정렬이 바뀌면 1페이지부터 다시 본다 — 조건이 달라진 목록에 옛 페이지 번호는 무의미하다.
  const applyFilters = (next: StoreProductsParams): void => {
    setSearchParams(toStoreSearchParams({ ...next, page: 1 }));
  };

  const goToPage = (page: number): void => {
    // 같은 페이지는 무시한다 — 동일 URL 을 히스토리에 쌓고 스크롤을 튕기지 않게.
    if (page === filters.page) {
      return;
    }

    setSearchParams(toStoreSearchParams({ ...filters, page }));
    window.scrollTo({ top: 0 });
  };

  // 조건이 바뀌어 페이지 수가 줄었을 때(공유 링크 등) 범위 밖 번호를 마지막 페이지로 되돌린다.
  const { freshPageCount } = productsPage;
  const isPageOutOfRange =
    freshPageCount !== null && freshPageCount > 0 && filters.page > freshPageCount;

  useEffect(() => {
    if (isPageOutOfRange && freshPageCount !== null) {
      setSearchParams(toStoreSearchParams({ ...filters, page: freshPageCount }), { replace: true });
    }
  }, [isPageOutOfRange, freshPageCount, filters, setSearchParams]);

  return (
    // 한국어는 어절 단위로 줄을 바꾼다 — 단어 중간에서 끊기지 않게(HOME 루트와 같은 규칙).
    <div className="flex min-h-dvh flex-col bg-background break-keep">
      <SiteHeader />

      <main className="flex-1 px-5 pt-12 pb-24 sm:px-8 sm:pt-16 sm:pb-32 xl:px-14">
        <header className="flex flex-col gap-4">
          {/* 보이는 제목은 영문 "Store" 한 단어, 보조기기에는 한국어 화면 이름을 함께 읽힌다. */}
          <h1 className="text-[clamp(2.5rem,5vw,4.5rem)] leading-none font-bold tracking-[-0.03em]">
            <span aria-hidden>Store</span>
            <span className="sr-only">상품 목록</span>
          </h1>
          <p className="text-[15px] text-muted-foreground sm:text-base">
            카테고리와 조건으로 만들 수 있는 굿즈를 찾아보세요.
          </p>
        </header>

        <div className="mt-10 sm:mt-14">
          <StoreCategoryTabs
            tree={categoryTreeQuery.data ?? []}
            isPending={categoryTreeQuery.isPending}
            isError={categoryTreeQuery.isError}
            onRetry={() => {
              void categoryTreeQuery.refetch();
            }}
            filters={filters}
            onFiltersChange={applyFilters}
          />
        </div>

        <section aria-label="상품 목록" aria-busy={isListLoading} className="mt-8 space-y-6">
          <div className="space-y-4">
            <StoreToolbar
              totalCount={productsPage.totalCount}
              filters={filters}
              onSortChange={(sort) => applyFilters({ ...filters, sort })}
              onOpenFilters={openFilterDrawer}
              isFilterDrawerOpen={isFilterDrawerOpen}
            />
            <StoreActiveFilters filters={filters} onFiltersChange={applyFilters} />
          </div>

          {isExhibitionTitlePending && <StoreExhibitionBannerPlaceholder />}
          {shouldShowListing && exhibitionQuery.data !== undefined && (
            <StoreExhibitionBanner
              title={exhibitionQuery.data.title}
              onClear={() => applyFilters({ ...filters, exhibitionId: undefined })}
            />
          )}

          {/* 상태 알림은 자리를 계속 지켜야 읽힌다 — 마운트했다 지우지 않고 문구만 바꾼다. */}
          <p role="status" aria-live="polite" className="sr-only">
            {isListLoading ? '상품 목록을 불러오는 중입니다.' : ''}
          </p>

          {isExhibitionEnded ? (
            <StoreExhibitionEnded />
          ) : productsPage.isPending ? (
            <ProductCardGridSkeleton />
          ) : productsPage.isFirstLoadError ? (
            // 원문 오류는 화면에 싣지 않는다 — 사용자용 문장과 재시도만 남긴다.
            <div role="alert" className="flex flex-col items-center gap-4 py-16 text-center">
              <p className="text-sm text-destructive">상품 목록을 불러오지 못했습니다.</p>
              <Button variant="outline" size="sm" onClick={productsPage.refetch}>
                다시 시도
              </Button>
            </div>
          ) : productsPage.products.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-16 text-center">
              <p className="text-sm text-muted-foreground">조건에 맞는 상품이 없습니다.</p>
              {/* 걸린 필터가 없으면 지울 것도 없다 — 드로어 초기화 버튼과 같은 조건. */}
              {hasActiveStoreFilters(filters) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyFilters({ sort: filters.sort, page: 1 })}
                >
                  필터 초기화
                </Button>
              )}
            </div>
          ) : (
            <ProductCardGrid products={productsPage.products} />
          )}

          {/* 페이지 이동 실패 — 같은 조건의 직전 목록을 유지한 채 수동 재시도만 남긴다. */}
          {shouldShowListing && productsPage.isPageLoadError && (
            <div role="alert" className="flex flex-col items-center gap-3 text-center">
              <p className="text-sm text-destructive">페이지를 불러오지 못했습니다.</p>
              <Button variant="outline" size="sm" onClick={productsPage.refetch}>
                다시 시도
              </Button>
            </div>
          )}

          {/* 한 페이지뿐이어도 1 을 보여 준다 — 목록이 비었을 때(0페이지)만 뺀다. */}
          {shouldShowListing && productsPage.pageCount !== null && productsPage.pageCount >= 1 && (
            <StorePagination
              page={filters.page}
              pageCount={productsPage.pageCount}
              onPageChange={goToPage}
            />
          )}
        </section>
      </main>

      <SiteFooter />

      <StoreFilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={closeFilterDrawer}
        filters={filters}
        onFiltersChange={applyFilters}
      />
    </div>
  );
};
